import React, { useState, useEffect } from 'react';
import { Card, Table, Badge, Spinner, Alert, Button } from 'react-bootstrap';
import { useAuth } from '../Context/AuthContext';
import axios from 'axios';
import "../Styling/ViewLeaves.css";
import { Base_URL } from '../url';
import { toast } from 'react-toastify';
import { FaDownload } from 'react-icons/fa';

const ViewLeaves = () => {
  const [normalLeaves, setNormalLeaves] = useState([]);
  const [wfhLeaves, setWfhLeaves] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [processingIds, setProcessingIds] = useState([]);
  const [pastLeaves, setPastLeaves] = useState([]);
const [fromDate, setFromDate] = useState("");
const [toDate, setToDate] = useState("");
const [pastLoading, setPastLoading] = useState(false);

  const { user } = useAuth();

useEffect(() => {
  loadAllData();

  const { from, to } = getCurrentMonthRange();
  setFromDate(from);
  setToDate(to);
  fetchPastLeaves(from, to);
}, []);
const handleDateFilter = () => {
  if (!fromDate || !toDate) {
    setError("Please select both From and To dates");
    return;
  }
  fetchPastLeaves(fromDate, toDate);
};


const fetchPastLeaves = async (from, to) => {
  try {
    setPastLoading(true);

    const response = await axios.get(
      `${Base_URL}/Leave/past-leaves`,
      {
        params: {
          orgId: user?.organisationId,
          fromDate: from,
          toDate: to
        },
        headers: {
          Authorization: `Bearer ${user?.token}`
        }
      }
    );

    setPastLeaves(response.data || []);
  } catch (err) {
    setError("Failed to load past leaves");
  } finally {
    setPastLoading(false);
  }
};

  const loadAllData = async () => {
    setLoading(true);
    setError(null);

    try {
      await fetchPendingLeaves();
      await fetchPendingWFHLeaves();
    } catch (err) {
      setError("Failed to load all leave data");
    } finally {
      setLoading(false);
    }
  };
  const getCurrentMonthRange = () => {
  const now = new Date();
  const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
  const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);

  return {
    from: firstDay.toISOString().split("T")[0],
    to: lastDay.toISOString().split("T")[0]
  };
};


  const fetchPendingLeaves = async () => {
    try {
      const response = await axios.get(
        `${Base_URL}/Leave/GetPendingLeaves?org_id=${user?.organisationId}`, 
        {
          headers: {
            Authorization: `Bearer ${user?.token}`
          }
        }
      );

      console.log("Pending Leaves Response:", response.data);
      
      // Filter out WFH leaves from normal leaves (backend might still return them)
      const filteredNormalLeaves = response.data.filter(leave => 
        leave.leaveType !== "WFH" && leave.leaveType !== "Work From Home"
      );
      
      setNormalLeaves(filteredNormalLeaves);
    } catch (error) {
      console.error("Error fetching normal leaves:", error);
      setError("Failed to load pending leaves");
    }
  };

  const fetchPendingWFHLeaves = async () => {
    try {
      const response = await axios.get(
        `${Base_URL}/Leave/getworkfromhomeleave?org_id=${user?.organisationId}`, 
        {
          headers: {
            Authorization: `Bearer ${user?.token}`
          }
        }
      );
      
      console.log("WFH Leaves Response:", response.data);
      
      // Get data from response.data.data (as per your backend structure)
      const wfhData = response.data.data || response.data;
      
      // Filter only pending WFH requests
      const pendingWFH = Array.isArray(wfhData) 
        ? wfhData.filter(wfh => wfh.leaveStatus === "Pending")
        : [];
      
      setWfhLeaves(pendingWFH);
    } catch (error) {
      console.error("Error fetching WFH leaves:", error);
      setError("Failed to load WFH requests");
    }
  };

  const getStatusVariant = (status) => {
    switch (status?.toLowerCase()) {
      case 'approved': 
      case 'confirmed': 
        return 'success';
      case 'rejected': return 'danger';
      case 'pending': return 'warning';
      case 'cancelled':
       return 'secondary';
      default: return 'secondary';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      return new Date(dateString).toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric"
      });
    } catch {
      return dateString;
    }
  };

  const setProcessing = (id, active) => {
    setProcessingIds(prev =>
      active ? [...prev, id] : prev.filter(x => x !== id)
    );
  };

  const isProcessing = (id) => processingIds.includes(id);

  // Handle normal leave approval
  const handleApprove = async (leaveId) => {
    try {
      setProcessing(leaveId, true);
      await axios.post(
        `${Base_URL}/Leave/ConfirmLeave/${leaveId}`, 
        null, 
        {
          headers: { 
            Authorization: `Bearer ${user?.token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      await loadAllData();
    } catch (error) {
      setError(`Failed to approve leave: ${error.message}`);
    } finally {
      setProcessing(leaveId, false);
    }
  };

  // Handle normal leave rejection
  const handleReject = async (leaveId) => {
    try {
      setProcessing(leaveId, true);
      await axios.post(
        `${Base_URL}/Leave/RejectLeave/id?id=${leaveId}`, 
        null, 
        {
          headers: { 
            Authorization: `Bearer ${user?.token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      await loadAllData();
    } catch (error) {
      setError(`Failed to reject leave: ${error.message}`);
    } finally {
      setProcessing(leaveId, false);
    }
  };

  // Handle WFH approval
  const handleApproveWFH = async (leaveId) => {
    try {
      setProcessing(leaveId, true);
      const response = await axios.post(
        `${Base_URL}/Leave/ConfirmWFH/${leaveId}`,
        null,
        {
          headers: { 
            Authorization: `Bearer ${user?.token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      console.log("WFH Approved:", response.data);
      await loadAllData();
    } catch (error) {
      setError(`Failed to approve WFH: ${error.message}`);
    } finally {
      setProcessing(leaveId, false);
    }
  };

  // Handle WFH rejection
  const handleRejectWFH = async (leaveId) => {
    try {
      setProcessing(leaveId, true);
      const response = await axios.post(
        `${Base_URL}/Leave/RejectWFH/${leaveId}`,
        null,
        {
          headers: { 
            Authorization: `Bearer ${user?.token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      console.log("WFH Rejected:", response.data);
      await loadAllData();
    } catch (error) {
      setError(`Failed to reject WFH: ${error.message}`);
    } finally {
      setProcessing(leaveId, false);
    }
  };
  const renderPastLeavesTable = () => (
  <Table responsive hover className="view-leaves-table">
    <thead className="view-leaves-table-header">
      <tr>
        <th>User</th>
        <th>Start Date</th>
        <th>End Date</th>
        <th>Duration</th>
        <th>Type</th>
        <th>Status</th>
        <th>Actions</th>
      </tr>
    </thead>

    <tbody>
      {pastLeaves.map((leave, index) => (
        <tr key={index} className="view-leaves-table-row">
        <td>{leave.userName || '-'}</td>
        <td>{formatDate(leave.leaveDate)}</td>
        <td>{formatDate(leave.endLeaveDate)}</td>
        <td>{leave.leaveDuration || 1} day(s)</td>
        <td>{leave.leaveType}</td>
      
        <td>
          <Badge bg={getStatusVariant(leave.leaveStatus)}>
            {leave.leaveStatus}
          </Badge>
        </td>
      
        <td>
          <Button
            size="sm"
            variant="danger"
onClick={() => handleCancelLeave(leave.leaveId)}
            disabled={
              isProcessing(leave.leaveId) ||
              ["Cancelled", "Rejected"].includes(leave.leaveStatus)
            }
          >
            {isProcessing(leave.leaveId) ? (
              <Spinner animation="border" size="sm" />
            ) : "Cancel"}
          </Button>
        </td>
      </tr>
      ))}
    </tbody>
  </Table>
);

const handleCancelLeave = async (leaveId) => {

  if (!window.confirm("Are you sure you want to cancel this leave?")) return;
  
  try {
    setProcessing(leaveId, true);

    await axios.post(
`${Base_URL}/Leave/CancelLeave/${leaveId}`,
      null,
      {
        headers: {
          Authorization: `Bearer ${user?.token}`,
          "Content-Type": "application/json",
        },
      }
    );

    // ✅ Instant UI update
    setPastLeaves((prev) =>
      prev.map((leave) =>
        leave.leaveId === leaveId
          ? { ...leave, leaveStatus: "Cancelled" }
          : leave
      )
    );

  } catch (error) {
    setError("Failed to cancel leave");
  } finally {
    setProcessing(leaveId, false);
  }
};
 // download past leaves data as Excel
const downloadPastLeavesExcel = async (orgId) => {
  try {
    const response = await axios.post(
      `${Base_URL}/Leave/DownloadPastLeavesExcel`,
      {
        orgId: orgId,
            fromDate: fromDate,
    toDate: toDate
      }, 
      {
        responseType: "blob", // important for file downloads
      },
    );
    return response;
  } catch (error) {
    throw error.response?.data || "Error downloading resource data!";
  }
};

  const handleDownloadExcel = async () => {
    setLoading(true);
    try {
      const orgId = user?.organisationId; 
      const response = await downloadPastLeavesExcel(orgId);
      if (!response.data) {
        throw new Error("No data received from server");
      }

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "Past_Leaves_Data.xlsx");
      document.body.appendChild(link);
      link.click();

      setTimeout(() => {
        window.URL.revokeObjectURL(url);
        link.parentNode.removeChild(link);
      }, 100);

      toast.success("Past Leaves data downloaded successfully!");
    } catch (error) {
      console.error("Error downloading past leaves data:", error);
      toast.error(`Error downloading past leaves data: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Render normal leaves table
  const renderNormalLeavesTable = () => (
    <Table responsive hover className="view-leaves-table">
      <thead className="view-leaves-table-header">
        <tr>
          <th>User</th>
          <th>Start Date</th>
          <th>End Date</th>
          <th>Duration</th>
          <th>Type</th>
          <th>Status</th>
          <th>Actions</th>
        </tr>
      </thead>

      <tbody>
        {normalLeaves.map((leave) => (
          <tr key={leave.leaveId} className="view-leaves-table-row">
            <td>{leave?.userName || '-'}</td>
            <td>{formatDate(leave.leaveDate)}</td>
            <td>{formatDate(leave.endLeaveDate)}</td>
            <td>{leave.leaveDuration || 1} day(s)</td>
            <td>{leave.leaveType}</td>

            <td>
              <Badge bg={getStatusVariant(leave.leaveStatus)}>
                {leave.leaveStatus || 'Pending'}
              </Badge>
            </td>

            <td>
              <div className="d-flex gap-2">
                <Button
                  size="sm"
                  variant="success"
                  onClick={() => handleApprove(leave.leaveId)}
                  disabled={isProcessing(leave.leaveId) || leave.leaveStatus !== 'Pending'}
                >
                  {isProcessing(leave.leaveId) ? (
                    <Spinner animation="border" size="sm" />
                  ) : 'Approve'}
                </Button>

                <Button
                  size="sm"
                  variant="danger"
                  onClick={() => handleReject(leave.leaveId)}
                  disabled={isProcessing(leave.leaveId) || leave.leaveStatus !== 'Pending'}
                >
                  {isProcessing(leave.leaveId) ? (
                    <Spinner animation="border" size="sm" />
                  ) : 'Reject'}
                </Button>
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </Table>
  );

  // Render WFH leaves table
  const renderWFHLeavesTable = () => (
    <Table responsive hover className="view-leaves-table">
      <thead className="view-leaves-table-header">
        <tr>
          <th>User</th>
          <th>Start Date</th>
          <th>End Date</th>
          <th>Duration</th>
          <th>Type</th>
          <th>Status</th>
          <th>Actions</th>
        </tr>
      </thead>

      <tbody>
        {wfhLeaves.map((wfh) => (
          <tr key={wfh.leaveId} className="view-leaves-table-row">
            <td>{wfh?.resourceName || '-'}</td>
            <td>{formatDate(wfh.leaveDate)}</td>
            <td>{formatDate(wfh.endLeaveDate)}</td>
            <td>{wfh.leaveDuration || 1} day(s)</td>
            <td>WFH</td>

            <td>
              <Badge bg={getStatusVariant(wfh.leaveStatus)}>
                {wfh.leaveStatus || 'Pending'}
              </Badge>
            </td>

            <td>
              <div className="d-flex gap-2">
                <Button
                  size="sm"
                  variant="success"
                  onClick={() => handleApproveWFH(wfh.leaveId)}
                  disabled={isProcessing(wfh.leaveId) || wfh.leaveStatus !== 'Pending'}
                >
                  {isProcessing(wfh.leaveId) ? (
                    <Spinner animation="border" size="sm" />
                  ) : 'Approve'}
                </Button>

                <Button
                  size="sm"
                  variant="danger"
                  onClick={() => handleRejectWFH(wfh.leaveId)}
                  disabled={isProcessing(wfh.leaveId) || wfh.leaveStatus !== 'Pending'}
                >
                  {isProcessing(wfh.leaveId) ? (
                    <Spinner animation="border" size="sm" />
                  ) : 'Reject'}
                </Button>
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </Table>
  );

  if (loading) {
    return (
      <div className="view-leaves-loading-container">
        <Spinner animation="border" />
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div className="view-leaves-container">
      {/* PAGE HEADER */}
      <div className="view-leaves-header">
        <h3 className="view-leaves-title">Leave Approval Panel</h3>
        <Button 
          variant="outline-primary" 
          size="sm"
          onClick={loadAllData}
          disabled={loading}
        >
          Refresh
        </Button>
      </div>

      {error && (
        <Alert variant="danger" className="view-leaves-alert">
          {error}
        </Alert>
      )}

      {/* Normal Leaves Section */}
      <Card className="view-leaves-card">
        <Card.Header className="section-title">
          Pending Normal Leaves ({normalLeaves.length})
        </Card.Header>
        <Card.Body>
          {normalLeaves.length === 0 ? (
            <p className="text-muted text-center">No pending leave requests</p>
          ) : (
            renderNormalLeavesTable()
          )}
        </Card.Body>
      </Card>

      {/* WFH Leaves Section */}
     {user?.organisationId!==2001 &&<Card className="view-leaves-card mt-4">
        <Card.Header className="section-title">
          Pending WFH Requests ({wfhLeaves.length})
        </Card.Header>
        <Card.Body>
          {wfhLeaves.length === 0 ? (
            <p className="text-muted text-center">No pending WFH requests</p>
          ) : (
            renderWFHLeavesTable()
          )}
        </Card.Body>
      </Card>}
      <Card className="view-leaves-card mt-4">
  <Card.Header className="section-title d-flex justify-content-between align-items-center">
    <span>Past Leaves</span>

    <div className="d-flex gap-2">
                  
                      {/* Download Button */}
                      <button
                        className="gradient-btn green-btn"
                        onClick={handleDownloadExcel}
                        disabled={loading}
                      >
                        {loading ? (
                          "Downloading..."
                        ) : (
                          <>
                            <span>
                              <FaDownload style={{ marginRight: "8px" }} />
                              Download Data
                            </span>
                          </>
                        )}
                      </button>
                
      <input
        type="date"
        className="form-control form-control-sm"
        value={fromDate}
        onChange={(e) => setFromDate(e.target.value)}
      />
      <input
        type="date"
        className="form-control form-control-sm"
        value={toDate}
        onChange={(e) => setToDate(e.target.value)}
      />
      <Button size="sm" variant="primary" onClick={handleDateFilter}>
        Filter
      </Button>
    </div>
  </Card.Header>

  <Card.Body>
    {pastLoading ? (
      <div className="text-center">
        <Spinner animation="border" />
      </div>
    ) : pastLeaves.length === 0 ? (
      <p className="text-muted text-center">No past leaves found</p>
    ) : (
      renderPastLeavesTable()
    )}
  </Card.Body>
</Card>

    </div>

    
  );
};

export default ViewLeaves;